import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaTruck, FaBox, FaSearch, FaFilter, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import AssistantSidebar from '../components/AssistantSidebar';
import { API_BASE_URL } from '../config/api';
import '../styles/SupplierDetails.css';

const SupplierDetails = ({ user, embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    companyName: '',
    assetName: ''
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;

  useEffect(() => {
    fetchSuppliersAndAssets();
  }, []);

  const fetchSuppliersAndAssets = async () => {
    try {
      setLoading(true);
      setError('');
      const token = sessionStorage.getItem('token');

      // Fetch all assets
      const assetsRes = await axios.get(`${API_BASE_URL}/api/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (assetsRes.data.success && assetsRes.data.data) {
        setAssets(assetsRes.data.data);
        setFilteredAssets(assetsRes.data.data); // Show all assets by default

        // Extract unique suppliers from assets for sidebar reference
        const uniqueSuppliers = {};
        assetsRes.data.data.forEach((asset) => {
          if (asset.supplier) {
            if (!uniqueSuppliers[asset.supplier]) {
              uniqueSuppliers[asset.supplier] = {
                name: asset.supplier,
                assetCount: 0,
                totalCost: 0
              };
            }
            uniqueSuppliers[asset.supplier].assetCount += 1;
            uniqueSuppliers[asset.supplier].totalCost += Number(asset.purchaseCost) || 0;
          }
        });

        // Items extracted but not used in rendering
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load suppliers');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    if (!assets.length) {
      setFilteredAssets([]);
      return;
    }

    let filtered = [...assets];

    // Filter by company/supplier name
    if (filters.companyName.trim()) {
      filtered = filtered.filter((asset) =>
        asset.supplier?.toLowerCase().includes(filters.companyName.toLowerCase())
      );
    }

    // Filter by asset name or serial number
    if (filters.assetName.trim()) {
      filtered = filtered.filter((asset) =>
        asset.assetName?.toLowerCase().includes(filters.assetName.toLowerCase()) ||
        asset.serialNumber?.toLowerCase().includes(filters.assetName.toLowerCase())
      );
    }

    setFilteredAssets(filtered);
  }, [assets, filters]);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, assets, applyFilters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };



  // Pagination logic
  const getPaginationData = () => {
    const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedAssets = filteredAssets.slice(startIndex, endIndex);

    return {
      paginatedAssets,
      totalPages,
      startIndex,
      endIndex
    };
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    const { totalPages } = getPaginationData();
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="sd-supplier-details-container">
        {!embedded && <AssistantSidebar user={user} />}
        <div className="sd-supplier-details-content">
          <div className="sd-loading">Loading suppliers...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="sd-supplier-details-container">
      {!embedded && <AssistantSidebar user={user} />}

      <div className={`sd-supplier-details-content ${embedded ? 'embedded' : ''}`}>
        {/* Header */}
        <div className="sd-supplier-header">
          <button className="sd-btn-back" onClick={() => navigate(`/lab-admin/dashboard?section=manage-suppliers`)}>
            <FaArrowLeft /> Back
          </button>
          <div className="sd-header-info">
            <h1>
              <FaTruck className="sd-header-icon" />
              Supplier Management
            </h1>
            <p>View and manage suppliers and their assets</p>
          </div>
        </div>

        {error && <div className="sd-error-message">{error}</div>}

        {/* Main Layout */}
   

          {/* Right Panel - All Assets with Filters */}
          <div className="sd-assets-panel">
            <div className="sd-assets-header">
              <div className="sd-assets-title-section">
                <h2>All Assets</h2>
                <p>Showing {filteredAssets.length} of {assets.length} assets</p>
              </div>

              {/* Filter Controls */}
              <div className="sd-asset-filters">
                <div className="sd-filter-group">
                  <FaFilter className="sd-filter-icon" />
                  <input
                    type="text"
                    name="companyName"
                    placeholder="Search by company name..."
                    value={filters.companyName}
                    onChange={handleFilterChange}
                    className="sd-filter-input"
                  />
                </div>
                <div className="sd-filter-group">
                  <FaSearch className="sd-filter-icon" />
                  <input
                    type="text"
                    name="assetName"
                    placeholder="Search by asset name or serial..."
                    value={filters.assetName}
                    onChange={handleFilterChange}
                    className="sd-filter-input"
                  />
                </div>
              </div>
            </div>

            {filteredAssets.length === 0 ? (
              <div className="sd-no-assets">
                <FaBox className="sd-empty-icon" />
                <p>{assets.length === 0 ? 'No assets found' : 'No assets match your filters'}</p>
              </div>
            ) : (
              <>
                <div className="sd-assets-table-wrapper">
                  <table className="sd-assets-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Company</th>
                        <th>Asset Name</th>
                        <th>Serial Number</th>
                        <th>Category</th>
                        <th>Purchase Date</th>
                        <th>Purchase Cost</th>
                        
                        <th>Warranty Expiry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginationData().paginatedAssets.map((asset, idx) => (
                        <tr key={asset._id} className="sd-asset-row">
                          <td className="sd-row-number">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                          <td className="sd-company-name">{asset.supplier || 'N/A'}</td>
                          <td className="sd-asset-name">{asset.assetName}</td>
                          <td className="sd-serial-number">{asset.serialNumber || 'N/A'}</td>
                          <td className="sd-category">{asset.category || 'N/A'}</td>
                          <td className="sd-purchase-date">{formatDate(asset.purchaseDate)}</td>
                          <td className="sd-purchase-cost">
                            <span className="sd-cost-badge">
                              {formatCurrency(asset.purchaseCost || 0)}
                            </span>
                          </td>
                          
                          <td className="sd-warranty-expiry">
                            {formatDate(asset.warrantyExpiryDate)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="sd-pagination-container">
                  <div className="sd-pagination-info">
                    <span>
                      Showing {getPaginationData().startIndex + 1} - {Math.min(getPaginationData().endIndex, filteredAssets.length)} of {filteredAssets.length}
                    </span>
                  </div>

                  <div className="sd-pagination-controls">
                    <button
                      className="sd-pagination-btn"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      <FaChevronLeft /> Previous
                    </button>

                    <div className="sd-pagination-pages">
                      {Array.from({ length: getPaginationData().totalPages }, (_, i) => i + 1).map((pageNum) => (
                        <button
                          key={pageNum}
                          className={`sd-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => handlePageClick(pageNum)}
                        >
                          {pageNum}
                        </button>
                      ))}
                    </div>

                    <button
                      className="sd-pagination-btn"
                      onClick={handleNextPage}
                      disabled={currentPage === getPaginationData().totalPages}
                    >
                      Next <FaChevronRight />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
       
      </div>
    </div>
  );
};

export default SupplierDetails;
